/**
 * Wanderers (จอมยุทธ์พเนจร, lane C0-NPC N4; data/wanderers.ts): simulated players on the plains. Each is a fighter of
 * the World's own kind (`rival`: the World walks them home, wanders them, fights back when struck, gets them up
 * again), and this system plays them: who is online by the hours curve, the goal each one follows by their
 * personality (meditating, hunting the monster grounds, the duelling ring, a market stall, errands to the elder,
 * idling on the plaza), their town chat, and their challenges to the player at the ring (three a day at most, always
 * refusable: taken up through the ring's own `duelChallenge`, systems/duelGround.ts). They grow a little each day.
 *
 * Deterministic: a stream seeded by the map's seed, World time only. Their kills pay the player nothing (the World
 * pays only the player's and the party's).
 */
import { DAY_SECONDS } from '../data/folk';
import { PLAINS_SIZE } from '../data/layouts/plains';
import { getArt, getSkill } from '../data/skills';
import {
  PERSONALITIES,
  WANDERER_NAMES,
  WANDERER_SCHOOLS,
  WANDERER_STALLS,
  WANDERERS,
  type Personality,
  type WandererGoal,
} from '../data/wanderers';
import type { Point } from '../map';
import { freeAt, nearestFloor } from '../path';
import { mulberry32, type Rng } from '../rng';
import type { Entity, World } from '../world';
import { memoryOf } from './npcMemory';
import type { SystemHost, WorldSystem } from './types';

export interface WandererDef {
  readonly id: string;
  readonly name: string;
  readonly nick: string;
  readonly female: boolean;
  readonly sprite: string;
  readonly art: string;
  readonly personality: Personality;
  /** Strength relative to the player's on day 0. */
  readonly strength: number;
}

export interface WandererMind {
  readonly def: WandererDef;
  e: Entity | null;
  goal: WandererGoal;
  goalT: number;
  spot: Point | null;
  swingT: number;
  /** Holds the legendary palm this week (systems/tournament.ts): it fights the monsters with it. */
  palm?: boolean;
}

/** A challenge the player may take up. */
export interface WandererDare {
  readonly id: number;
  readonly until: number;
}

const SYSTEMS = new WeakMap<World, WanderersSystem>();
export const wanderersOf = (w: World): WanderersSystem | undefined => SYSTEMS.get(w);

const PERS: readonly Personality[] = ['hot', 'calm', 'sly', 'playful', 'shy', 'trader'];
const GOALS: readonly WandererGoal[] = ['meditate', 'hunt', 'ring', 'stall', 'errand', 'idle'];

export class WanderersSystem implements WorldSystem {
  readonly name = 'wanderers';
  readonly minds: WandererMind[] = [];
  dare: WandererDare | null = null;
  private rnd: Rng = () => 0.5;
  private made = false;
  private rollT = 0;
  private chatT = 0;
  private dareT = 10;
  private big = false;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    this.made = false;
  }

  /** The plains' wanderers, every one of them made up the same way each run. */
  private make(): void {
    this.made = true;
    const m = this.h.world.map;
    if (m.zone !== 'plains') return;
    this.big = m.w === PLAINS_SIZE;
    this.rnd = mulberry32(((m.seed | 0) * 104729 + 0x77a) >>> 0);
    const n = this.big ? WANDERERS.count.big : WANDERERS.count.small;
    const N = WANDERER_NAMES;
    const used = new Set<string>();
    for (let i = 0; i < n; i++) {
      const female = i % 2 === 1;
      const school = WANDERER_SCHOOLS[i % WANDERER_SCHOOLS.length]!;
      let name = '';
      for (let k = 0; k < 20 && (!name || used.has(name)); k++)
        name = this.pick(N.family) + this.pick(female ? N.female : N.male);
      used.add(name);
      const [lo, hi] = WANDERERS.strength;
      this.minds.push({
        def: {
          id: `w${i}`,
          name,
          nick: this.pick(N.nickA) + this.pick(N.nickB),
          female,
          sprite: female ? school.female : school.male,
          art: school.art,
          personality: PERS[(i * 5 + Math.floor(i / 6)) % PERS.length]!,
          strength: lo + this.rnd() * (hi - lo),
        },
        e: null,
        goal: 'idle',
        goalT: 0,
        spot: null,
        swingT: 0,
      });
    }
    this.chatT = this.range(WANDERERS.chatEvery);
  }

  private pick<T>(xs: readonly T[]): T {
    return xs[Math.floor(this.rnd() * xs.length)]!;
  }

  private range([a, b]: readonly [number, number]): number {
    return a + this.rnd() * (b - a);
  }

  step(dt: number): void {
    if (!this.made) this.make();
    if (!this.minds.length) return;
    const w = this.h.world;
    if ((this.rollT -= dt) <= 0) {
      this.rollT = WANDERERS.rollEvery;
      this.roll();
    }
    for (const m of this.minds) if (m.e) this.think(m, dt);
    if ((this.chatT -= dt) <= 0) {
      this.chatT = this.range(WANDERERS.chatEvery);
      this.chat();
    }
    if (this.dare && w.time > this.dare.until) this.dare = null;
    if ((this.dareT -= dt) <= 0) {
      this.dareT = 20;
      this.offerDare();
    }
  }

  /** How many are online now: the hours curve, less one for each other real player in the map. */
  wantOnline(): number {
    const w = this.h.world;
    const share = WANDERERS.curve[Math.floor(((w.hour % 24) + 24) % 24)] ?? 0.5;
    return Math.max(0, Math.min(this.minds.length, Math.round(this.minds.length * share) - (w.players.size - 1)));
  }

  /** Brings wanderers online and takes them off, a few at a time, in a turn that moves round with the hours. */
  private roll(): void {
    const w = this.h.world;
    const n = this.minds.length;
    const want = this.wantOnline();
    const from = Math.floor(w.hour) * 3;
    const on = new Set<number>();
    for (let k = 0; k < want; k++) on.add((from + k) % n);
    const p = w.player;
    this.minds.forEach((m, i) => {
      if (on.has(i) && !m.e) this.login(m);
      else if (!on.has(i) && m.e && Math.hypot(m.e.x - p.x, m.e.y - p.y) > 24 && w.duel?.foeId !== m.e.id) this.logout(m);
    });
  }

  private days(): number {
    return Math.floor(this.h.world.time / DAY_SECONDS);
  }

  private login(m: WandererMind): void {
    const w = this.h.world;
    const p = w.player;
    const map = w.map;
    this.choose(m);
    const at = this.floor(m.spot ?? map.plaza, 3) ?? { x: map.plaza.x, y: map.plaza.y };
    const d = m.def;
    const e = this.h.add({
      kind: 'rival',
      x: at.x,
      y: at.y,
      name: d.name,
      title: `${WANDERERS.mark} ${d.nick}`,
      sprite: d.sprite,
      speed: p.speed,
      level: p.level,
      rival: {
        homeX: at.x,
        homeY: at.y,
        aggroT: 0,
        skillT: 2,
        skills: getArt(d.art)
          .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
          .slice(0, 4),
      },
      wander: { cx: at.x, cy: at.y, r: 2.5, t: 1, tx: null, ty: null },
    });
    // As strong as their play has made them: a spread round the player's, a little more each day.
    const k = d.strength * (1 + WANDERERS.growth * this.days());
    Object.assign(e, {
      maxHp: Math.round(p.maxHp * k),
      hp: Math.round(p.maxHp * k),
      maxQi: p.maxQi,
      qi: p.maxQi,
      atk: p.atk * k,
      def: p.def * k,
      magic: p.magic * k,
      magicDef: p.magicDef * k,
      hit: p.hit,
      magicHit: p.magicHit,
      dodge: p.dodge,
      crit: p.crit,
      magicCrit: p.magicCrit,
      toughness: p.toughness,
      maxStamina: p.maxStamina,
      stamina: p.maxStamina,
    });
    m.e = e;
  }

  private logout(m: WandererMind): void {
    if (!m.e) return;
    if (this.dare?.id === m.e.id) this.dare = null;
    this.h.world.entities.delete(m.e.id);
    m.e = null;
  }

  /** A new goal by their personality, and its place on this map. */
  private choose(m: WandererMind): void {
    const pd = PERSONALITIES[m.def.personality];
    let r = this.rnd() * GOALS.reduce((a, g) => a + pd.goals[g], 0);
    let goal: WandererGoal = 'idle';
    for (const g of GOALS)
      if ((r -= pd.goals[g]) <= 0) {
        goal = g;
        break;
      }
    m.goal = goal;
    m.goalT = this.range(WANDERERS.goalFor);
    m.spot = this.spotFor(goal);
  }

  private spotFor(goal: WandererGoal): Point | null {
    const map = this.h.world.map;
    const c = map.plaza;
    const ring = (x: number, y: number, r: number): Point => {
      const a = this.rnd() * Math.PI * 2;
      return { x: x + Math.cos(a) * r, y: y + Math.sin(a) * r };
    };
    const region = (id: 'wolves' | 'bandits'): Point | null => {
      const g = map.regions?.[id];
      if (!g) return null;
      const [i0, i1, j0, j1] = g;
      // The rim nearest the town: where a hunt starts.
      return { x: Math.max(i0 + 2, Math.min(i1 - 2, c.x)), y: Math.max(j0 + 2, Math.min(j1 - 2, c.y)) };
    };
    switch (goal) {
      case 'meditate':
        return this.big ? ring(204.5, 156.5, 2) : ring(c.x + 12, c.y - 12, 1.5);
      case 'hunt':
        return region(this.rnd() < 0.5 ? 'wolves' : 'bandits');
      case 'ring':
        return map.duel ? ring(map.duel.x, map.duel.y + 4, 3) : ring(c.x, c.y, 8);
      case 'stall': {
        const s = this.pick(this.big ? WANDERER_STALLS.big : WANDERER_STALLS.small);
        return this.big ? { x: s[0] + 0.5, y: s[1] + 0.5 } : { x: c.x + s[0], y: c.y + s[1] };
      }
      case 'errand':
        return map.elder ? { x: map.elder.x + 1.5, y: map.elder.y + 1.5 } : ring(c.x, c.y, 6);
      case 'idle':
        return ring(c.x, c.y, 9 + this.rnd() * 3);
    }
  }

  private floor(p: Point, r = 3): Point | null {
    const map = this.h.world.map;
    if (freeAt(map, p.x, p.y)) return p;
    const a = nearestFloor(map, p.x, p.y, r);
    return a ? { x: a[0], y: a[1] } : null;
  }

  /** One wanderer's turn: on to the next goal when this one is done; hunting, trading, meditating at its place. */
  private think(m: WandererMind, dt: number): void {
    const e = m.e!;
    const b = e.rival!;
    m.goalT -= dt;
    if (m.goalT <= 0 && !(this.dare?.id === e.id)) {
      this.choose(m);
      const at = m.spot ? this.floor(m.spot) : null;
      if (at) {
        b.homeX = at.x;
        b.homeY = at.y;
        e.path = null;
        e.emote = null;
        e.wander = { cx: at.x, cy: at.y, r: 2.5, t: 0, tx: at.x, ty: at.y };
      }
    }
    if (e.dead || e.combatT > 0 || b.aggroT > 0) return;
    const there = Math.hypot(e.x - b.homeX, e.y - b.homeY) < 3;
    if (m.goal === 'hunt' && there) return this.hunt(m, dt);
    if (!there || e.moving) return;
    const want = m.goal === 'meditate' ? 'life-meditate' : m.goal === 'stall' ? 'life-hawk' : null;
    if (want && e.emote !== want) {
      e.emote = want;
      e.emoteT = 0;
      e.wander = null;
    }
  }

  /** At the monster grounds: after the nearest beast nobody else is fighting, a blow every so often. */
  private hunt(m: WandererMind, dt: number): void {
    const e = m.e!;
    const w = this.h.world;
    const p = w.player;
    m.swingT -= dt;
    let foe: Entity | null = null;
    let bd: number = WANDERERS.huntR;
    for (const o of w.entities.values()) {
      if (o.kind !== 'monster' || o.dead || !o.brain) continue;
      // Never a boss, nor the player's fight.
      if (o.brain.defId === 'boss' || o.brain.defId === 'worldboss' || o.targetId === p.id) continue;
      if (Math.abs(o.x - e.x) > bd || Math.abs(o.y - e.y) > bd) continue;
      const d = Math.hypot(o.x - e.x, o.y - e.y);
      if (d < bd) [foe, bd] = [o, d];
    }
    if (!foe) return;
    this.h.face(e, foe);
    if (bd > 1.6) {
      const s = Math.min(bd - 1.3, e.speed * dt);
      e.moving = this.h.move(e, ((foe.x - e.x) / bd) * s, ((foe.y - e.y) / bd) * s);
      return;
    }
    if (m.swingT > 0) return;
    m.swingT = WANDERERS.huntEvery;
    e.swingT = 0.22;
    e.combatT = 0.5;
    if (m.palm) {
      // The week's holder of the golden dragon palm (data/legendary.ts): full power on monsters.
      this.h.emit({ type: 'skill', srcId: e.id, skillId: 'dp9-burst', tgtId: foe.id });
      this.h.hit(e, foe, getSkill('dp9-burst').power, 'attack', 'dp9-burst');
      return;
    }
    this.h.emit({ type: 'basic', srcId: e.id, tgtId: foe.id, step: Math.floor(this.rnd() * 3) });
    this.h.hit(e, foe, 1, 'attack', 'basic');
  }

  /** A line in the town chat from one of those online, as their personality speaks. */
  private chat(): void {
    const on = this.minds.filter((m) => m.e);
    if (!on.length) return;
    let r = this.rnd() * on.reduce((a, m) => a + PERSONALITIES[m.def.personality].chatty, 0);
    let who = on[0]!;
    for (const m of on)
      if ((r -= PERSONALITIES[m.def.personality].chatty) <= 0) {
        who = m;
        break;
      }
    const text = this.pick(PERSONALITIES[who.def.personality].chat).replaceAll('{name}', this.h.world.progress.name);
    this.h.emit({ type: 'zoneSay', id: who.e!.id, name: who.def.name, text });
  }

  /** Challenges given today (a saved count, by the progress's day). */
  private daresToday(): number {
    const w = this.h.world;
    const day = w.progress.arena?.day || `d${this.days()}`;
    const [d, n] = (memoryOf(w.progress).giftDay['wanderers#dares'] ?? '').split('|');
    return d === day ? Number(n) || 0 : 0;
  }

  private countDare(): void {
    const w = this.h.world;
    const day = w.progress.arena?.day || `d${this.days()}`;
    memoryOf(w.progress).giftDay['wanderers#dares'] = `${day}|${this.daresToday() + 1}`;
  }

  /** At the ring, a wanderer there may challenge the player (three a day at most; the player may ignore it). */
  private offerDare(): void {
    const w = this.h.world;
    const p = w.player;
    if (this.dare || w.duel || p.dead || p.combatT > 0 || !w.map.duel) return;
    if (Math.hypot(p.x - w.map.duel.x, p.y - w.map.duel.y) > WANDERERS.dareRing) return;
    if (this.daresToday() >= WANDERERS.daresPerDay) return;
    const at = this.minds.filter((m) => m.e && !m.e.dead && m.goal === 'ring');
    if (!at.length) return;
    const m = this.pick(at);
    if (this.rnd() >= PERSONALITIES[m.def.personality].challenge) return;
    const e = m.e!;
    this.dare = { id: e.id, until: w.time + WANDERERS.dareOpen };
    this.countDare();
    // Over to the player, to ask.
    const to = this.floor({ x: p.x + 1.4, y: p.y + 0.6 }) ?? { x: p.x, y: p.y };
    Object.assign(e.rival!, { homeX: to.x, homeY: to.y });
    e.path = null;
    e.emote = null;
    m.goalT = Math.max(m.goalT, WANDERERS.dareOpen);
    this.h.emit({ type: 'bark', id: e.id, text: this.pick(PERSONALITIES[m.def.personality].dare) });
    this.h.notice(`${e.name}ท้าประลอง · คลิกที่เขาแล้วคลิกขวาที่รูปเพื่อรับคำท้า (ไม่รับก็ได้)`);
  }

  /** The wanderer behind an entity. */
  mindOf(e: Entity): WandererMind | undefined {
    return this.minds.find((m) => m.e === e);
  }
}
